import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { API_BASE_URL } from '../config/api';

// Create axios instance with auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function RedirectsAdminPage() {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingRedirect, setEditingRedirect] = useState(null);
  const [formData, setFormData] = useState({
    localPath: '',
    targetUrl: '',
    password: '',
    hasPassword: false
  });
  const [saving, setSaving] = useState(false);
  
  // Path validation state
  const [pathValidation, setPathValidation] = useState({
    checking: false,
    available: null,
    message: '',
    conflict: null
  });
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, redirect: null });

  useEffect(() => {
    document.title = 'Redirects Admin - Design Shop';
    fetchRedirects();
  }, []);

  // Check path availability when localPath changes
  useEffect(() => {
    const checkPathAvailability = async () => {
      const path = formData.localPath?.trim();
      
      // Don't check if path is empty
      if (!path || path.length === 0) {
        setPathValidation({ checking: false, available: null, message: '', conflict: null });
        return;
      }
      
      // Ensure path starts with /
      const normalizedPath = path.startsWith('/') ? path : '/' + path;
      
      // If editing existing redirect and path hasn't changed, don't check
      if (editingRedirect && editingRedirect.localPath === normalizedPath) {
        setPathValidation({ checking: false, available: true, message: '', conflict: null });
        return;
      }
      
      // Debounce the check
      const timeoutId = setTimeout(async () => {
        try {
          setPathValidation({ checking: true, available: null, message: '', conflict: null });
          
          // Remove leading slash for API endpoint
          const pathWithoutSlash = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
          const response = await authAxios.get(`${API_BASE_URL}/admin/redirects/check-path/${encodeURIComponent(pathWithoutSlash)}`);
          
          if (response.data.success) {
            const available = response.data.available;
            setPathValidation({
              checking: false,
              available: available,
              message: available 
                ? 'Path is available' 
                : (response.data.message || 'Path is not available'),
              conflict: response.data.conflict || null
            });
          } else {
            setPathValidation({
              checking: false,
              available: false,
              message: response.data.error || 'Error checking path',
              conflict: null
            });
          }
        } catch (err) {
          setPathValidation({
            checking: false,
            available: false,
            message: err.response?.data?.error || err.response?.data?.message || 'Error checking path availability',
            conflict: err.response?.data?.conflict || null
          });
        }
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timeoutId);
    };
    
    checkPathAvailability();
  }, [formData.localPath, editingRedirect]);

  const fetchRedirects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/redirects`);
      if (response.data.success) {
        setRedirects(response.data.data || []);
      } else {
        setError('Failed to load redirects');
      }
    } catch (err) {
      console.error('Error fetching redirects:', err);
      setError(err.response?.data?.error || 'Failed to load redirects');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRedirect(null);
    setFormData({
      localPath: '',
      targetUrl: '',
      password: '',
      hasPassword: false
    });
    setPathValidation({ checking: false, available: null, message: '', conflict: null });
    setShowForm(true);
  };

  const handleEdit = (redirect) => {
    setEditingRedirect(redirect);
    setFormData({
      localPath: redirect.localPath || '',
      targetUrl: redirect.targetUrl || '',
      password: '', // Don't show existing password
      hasPassword: redirect.hasPassword || false
    });
    setPathValidation({ checking: false, available: true, message: '', conflict: null });
    setShowForm(true);
  };

  const handleDelete = (redirect) => {
    setDeleteConfirm({ open: true, redirect });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.redirect) return;
    
    try {
      setSaving(true);
      const response = await authAxios.delete(`${API_BASE_URL}/admin/redirects/${deleteConfirm.redirect.id}`);
      if (response.data.success) {
        setToast({ message: 'Redirect deleted successfully', type: 'success' });
        fetchRedirects();
      } else {
        setToast({ message: 'Failed to delete redirect', type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting redirect:', err);
      setToast({ message: err.response?.data?.error || 'Failed to delete redirect', type: 'error' });
    } finally {
      setSaving(false);
      setDeleteConfirm({ open: false, redirect: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.localPath || !formData.targetUrl) {
      setToast({ message: 'Local path and target URL are required', type: 'error' });
      return;
    }
    
    // Ensure local path starts with /
    const localPath = formData.localPath.startsWith('/') ? formData.localPath : '/' + formData.localPath;
    
    // Check if path is available (if validation hasn't completed or shows conflict)
    if (pathValidation.checking) {
      setToast({ message: 'Please wait for path validation to complete', type: 'error' });
      return;
    }
    
    if (pathValidation.available === false) {
      setToast({ 
        message: pathValidation.message || 'Path is not available. Please choose a different path.', 
        type: 'error' 
      });
      return;
    }
    
    try {
      setSaving(true);
      
      if (editingRedirect) {
        // Update existing redirect
        const updateData = {
          localPath,
          targetUrl: formData.targetUrl
        };
        
        // Only update password if hasPassword changed or password is provided
        if (formData.hasPassword && formData.password) {
          updateData.password = formData.password;
        } else if (!formData.hasPassword) {
          // Remove password protection
          updateData.password = '';
        }
        
        const response = await authAxios.put(`${API_BASE_URL}/admin/redirects/${editingRedirect.id}`, updateData);
        if (response.data.success) {
          setToast({ message: 'Redirect updated successfully', type: 'success' });
          setShowForm(false);
          fetchRedirects();
        } else {
          setToast({ message: 'Failed to update redirect', type: 'error' });
        }
      } else {
        // Create new redirect
        const createData = {
          localPath,
          targetUrl: formData.targetUrl,
          password: formData.hasPassword && formData.password ? formData.password : undefined
        };
        
        const response = await authAxios.post(`${API_BASE_URL}/admin/redirects`, createData);
        if (response.data.success) {
          setToast({ message: 'Redirect created successfully', type: 'success' });
          setShowForm(false);
          fetchRedirects();
        } else {
          setToast({ message: 'Failed to create redirect', type: 'error' });
        }
      }
    } catch (err) {
      console.error('Error saving redirect:', err);
      setToast({ message: err.response?.data?.error || err.response?.data?.details || 'Failed to save redirect', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <div className="text-center">Loading redirects...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <AdminTabs />

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Redirects Management</h1>
          <button
            onClick={handleAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            Add Redirect
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingRedirect ? 'Edit Redirect' : 'Add New Redirect'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Local Path <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.localPath}
                    onChange={(e) => setFormData({ ...formData, localPath: e.target.value })}
                    placeholder="/Alabama"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      pathValidation.available === false
                        ? 'border-red-300 focus:ring-red-500'
                        : pathValidation.available === true
                        ? 'border-green-300 focus:ring-green-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  <div className="mt-1">
                    {pathValidation.checking && (
                      <p className="text-xs text-gray-500">Checking path availability...</p>
                    )}
                    {!pathValidation.checking && pathValidation.message && (
                      <p className={`text-xs ${
                        pathValidation.available === false
                          ? 'text-red-600'
                          : pathValidation.available === true
                          ? 'text-green-600'
                          : 'text-gray-500'
                      }`}>
                        {pathValidation.message}
                        {pathValidation.conflict === 'system' && (
                          <span className="block mt-1 font-medium">⚠️ This path is a protected system route and cannot be used</span>
                        )}
                        {pathValidation.conflict === 'note' && (
                          <span className="block mt-1 font-medium">⚠️ This path conflicts with an existing note slug</span>
                        )}
                        {pathValidation.conflict === 'redirect' && (
                          <span className="block mt-1 font-medium">⚠️ This path is already used by another redirect</span>
                        )}
                      </p>
                    )}
                    {!pathValidation.checking && !pathValidation.message && (
                      <p className="text-xs text-gray-500">Path that will trigger the redirect (must start with /)</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.targetUrl}
                    onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
                    placeholder="https://example.com or /about"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Full URL (http:// or https://) or local path (e.g. /about, /contact)</p>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.hasPassword}
                      onChange={(e) => setFormData({ ...formData, hasPassword: e.target.checked, password: '' })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Password Protected</span>
                  </label>
                </div>

                {formData.hasPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password {editingRedirect && formData.hasPassword ? '(leave blank to keep existing)' : ''}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required={!editingRedirect || !formData.hasPassword}
                    />
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingRedirect ? 'Update Redirect' : 'Create Redirect'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Local Path
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Password
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clicks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {redirects.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No redirects found. Click "Add Redirect" to create one.
                  </td>
                </tr>
              ) : (
                redirects.map((redirect) => (
                  <tr key={redirect.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <a
                        href={redirect.localPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={(e) => {
                          // Allow the redirect to work naturally
                          // The link will navigate to the path, which will trigger the redirect
                        }}
                      >
                        {redirect.localPath}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <a
                        href={redirect.targetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 truncate block max-w-md"
                      >
                        {redirect.targetUrl}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {redirect.hasPassword ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Protected
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {redirect.clickCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {redirect.createdAt ? new Date(redirect.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(redirect)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(redirect)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationDialog
        open={deleteConfirm.open}
        title="Delete Redirect"
        message={`Are you sure you want to delete the redirect from "${deleteConfirm.redirect?.localPath}" to "${deleteConfirm.redirect?.targetUrl}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false, redirect: null })}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default RedirectsAdminPage;
